import { useEffect, useState } from "react";
import { Utensils, UtensilsCrossed, ChevronRight, Info } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { GRADE_META } from "@/lib/rating";
import { useI18n } from "@/i18n/I18nContext";
import { api } from "@/lib/api";

const ORDER = ["excellent", "good", "bad", "awful"];

export default function Overview() {
  const { t } = useI18n();
  const [counts, setCounts] = useState({ excellent: 0, good: 0, bad: 0, awful: 0 });
  const [demo, setDemo] = useState(false);
  const [tab, setTab] = useState("food");

  useEffect(() => {
    api.get("/overview").then((r) => { setCounts(r.data.counts); setDemo(r.data.demo); }).catch(() => {});
  }, []);

  const total = ORDER.reduce((s, k) => s + counts[k], 0) || 1;
  let acc = 0;
  const stops = ORDER.map((k) => {
    const start = (acc / total) * 360;
    acc += counts[k];
    const end = (acc / total) * 360;
    return `${GRADE_META[k].solid} ${start}deg ${end}deg`;
  }).join(", ");

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-1">
        <span />
        <button onClick={() => toast.info(t("overview.infoTip"))} className="text-emerald-600"><Info className="w-5 h-5" /></button>
      </div>
      <h1 className="font-heading text-4xl font-extrabold text-slate-900 mb-1">{t("overview.title")}</h1>
      <p className="text-slate-400 text-sm mb-6">{demo ? t("overview.demoData") : t("overview.allTime")}</p>

      <div className="flex items-center justify-center gap-4 mb-6">
        <Utensils className="w-9 h-9 text-emerald-300" />
        <div className="relative w-52 h-52" data-testid="overview-pie">
          <div className="w-full h-full rounded-full" style={{ background: `conic-gradient(${stops})` }} />
          <div className="absolute inset-[22%] bg-slate-50 rounded-full flex flex-col items-center justify-center">
            <span className="font-heading text-3xl font-extrabold text-slate-900">{total}</span>
            <span className="text-xs text-slate-400">{t("overview.products")}</span>
          </div>
        </div>
        <UtensilsCrossed className="w-9 h-9 text-emerald-300" />
      </div>

      <div className="grid grid-cols-2 bg-slate-100 rounded-full p-1 mb-6">
        <button onClick={() => setTab("food")} data-testid="overview-tab-food" className={`rounded-full py-2 text-sm font-semibold transition-colors ${tab === "food" ? "bg-white shadow text-slate-900" : "text-slate-400"}`}>{t("overview.food")}</button>
        <button onClick={() => setTab("beauty")} data-testid="overview-tab-beauty" className={`rounded-full py-2 text-sm font-semibold transition-colors ${tab === "beauty" ? "bg-white shadow text-slate-900" : "text-slate-400"}`}>{t("overview.beauty")}</button>
      </div>

      {tab === "beauty" ? (
        <p className="text-center text-slate-400 py-8">{t("overview.beautySoon")}</p>
      ) : (
        <>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">{t("overview.gradeOverview")}</p>
          <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            {ORDER.map((k) => (
              <div key={k} data-testid={`grade-row-${k}`} className="flex items-center gap-3 px-4 py-3.5">
                <span className="w-3 h-3 rounded-full" style={{ background: GRADE_META[k].solid }} />
                <span className="flex-1 font-medium text-slate-800">{t(`grade.${k}`)}</span>
                <span className="font-heading font-bold text-slate-900">{counts[k]}</span>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}
